import React, { useCallback, useEffect, useState } from "react";
import { ClipboardList, Loader2, RefreshCw, Search, X } from "lucide-react";
import { Layout } from "../../layouts/DashboardLayout";
import useToken from "../../../../hooks/auth/useToken";
import { DashboardAuditLog, dashboardAuditService } from "../../services/dashboardAuditService";

const ACTION_LABELS: Record<string, string> = {
  WALLET_RECHARGE: "Recharge wallet",
  WALLET_DEBIT: "Débit wallet",
  WALLET_HISTORY_STATUS: "Statut historique wallet",
  PAYMENT_STATUS: "Statut transaction",
  PAYMENT_RETRY: "Relance payout",
  TRACKING_CREATE: "Création suivi",
  TRACKING_UPDATE: "Modification suivi",
  TRACKING_DELETE: "Suppression suivi",
};

const MODULE_LABELS: Record<string, string> = {
  wallets: "Wallets",
  transactions: "Transactions",
};

const PAGE_SIZE = 25;

const DashboardAuditPage: React.FC = () => {
  const { token } = useToken();
  const [items, setItems] = useState<DashboardAuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [moduleFilter, setModuleFilter] = useState("");
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [selected, setSelected] = useState<DashboardAuditLog | null>(null);

  const loadLogs = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await dashboardAuditService.list(token, {
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
        module: moduleFilter || undefined,
        search: appliedSearch || undefined,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        "Impossible de charger le journal d'activité";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [token, page, moduleFilter, appliedSearch]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Layout>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="mb-1 text-3xl font-bold text-gray-800">Journal d'activité</h1>
          <p className="text-gray-600">
            Historique des actions réalisées par les administrateurs (Transactions et Wallets)
          </p>
        </div>
        <button
          onClick={loadLogs}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Actualiser
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setPage(0);
                setAppliedSearch(search.trim());
              }
            }}
            placeholder="Nom, email, description, ID..."
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={moduleFilter}
          onChange={(e) => {
            setPage(0);
            setModuleFilter(e.target.value);
          }}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Tous les modules</option>
          <option value="transactions">Transactions</option>
          <option value="wallets">Wallets</option>
        </select>
        <button
          onClick={() => {
            setPage(0);
            setAppliedSearch(search.trim());
          }}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Filtrer
        </button>
      </div>

      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Date</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Auteur</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Module</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Action</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Détail</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                    <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin" />
                    Chargement...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                    <ClipboardList className="mx-auto mb-2 h-8 w-8 text-gray-300" />
                    Aucune action enregistrée
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id} onClick={() => setSelected(item)} className="cursor-pointer hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                      {item.created_at ? new Date(item.created_at).toLocaleString("fr-FR") : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{item.actor_name || "Inconnu"}</div>
                      <div className="text-xs text-gray-500">{item.actor_email || item.actor_id}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                        {MODULE_LABELS[item.module] || item.module}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-800">{ACTION_LABELS[item.action] || item.action}</td>
                    <td className="max-w-xs truncate px-4 py-3 text-gray-600">
                      {item.description || item.resource_id || "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-500">{item.ip_address || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between border-t px-4 py-3 text-sm text-gray-600">
            <span>
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} sur {total}
            </span>
            <div className="flex gap-2">
              <button
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="rounded border px-3 py-1 disabled:opacity-40"
              >
                Précédent
              </button>
              <span>
                {page + 1} / {totalPages}
              </span>
              <button
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded border px-3 py-1 disabled:opacity-40"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Détail de l'action</h2>
                <p className="text-sm text-gray-500">{ACTION_LABELS[selected.action] || selected.action}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-gray-500">Auteur</dt>
                <dd className="font-medium text-gray-900">{selected.actor_name || "—"}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Email</dt>
                <dd className="font-medium text-gray-900">{selected.actor_email || "—"}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Rôle</dt>
                <dd className="font-medium text-gray-900">{selected.actor_role || "—"}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Date</dt>
                <dd className="font-medium text-gray-900">
                  {selected.created_at ? new Date(selected.created_at).toLocaleString("fr-FR") : "—"}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Module</dt>
                <dd className="font-medium text-gray-900">{MODULE_LABELS[selected.module] || selected.module}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Ressource</dt>
                <dd className="break-all font-medium text-gray-900">
                  {selected.resource_type} {selected.resource_id ? `· ${selected.resource_id}` : ""}
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-gray-500">Description</dt>
                <dd className="font-medium text-gray-900">{selected.description || "—"}</dd>
              </div>
              <div>
                <dt className="text-gray-500">IP</dt>
                <dd className="font-medium text-gray-900">{selected.ip_address || "—"}</dd>
              </div>
            </dl>
            {selected.details && (
              <pre className="mt-4 overflow-x-auto rounded-lg bg-gray-50 p-3 text-xs text-gray-700">
                {JSON.stringify(selected.details, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default DashboardAuditPage;
